import { useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ApiError } from '../api/client'
import { useAccounts, useCreateAccount, useUpdateAccount } from '../api/hooks'
import type { Account, AccountType, CoverageState } from '../api/types'
import { CoverageBadge, formatPercent } from '../components/Badges'
import { ClassificationEditor } from '../components/ClassificationEditor'

const COVERAGE_FILTERS: { value: CoverageState | null; label: string; hint: string }[] = [
  { value: null, label: 'All', hint: 'Every account' },
  { value: 'None', label: 'Unmapped', hint: 'No driver map at all — QC-A001 blocks a run if a balance exists' },
  { value: 'Partial', label: 'Partial', hint: 'Driver percentages do not total 100 — QC-A002 blocks the run' },
  { value: 'Complete', label: 'Complete', hint: 'Percentages total exactly 100' },
  { value: 'NotAllocable', label: 'Not allocable', hint: 'Excluded from allocation by design' },
]

const ACCOUNT_TYPES: AccountType[] = ['Expense', 'Revenue', 'Asset', 'Liability', 'Equity']

const ROW_HEIGHT = 28

/**
 * Screen 2 — Chart of Accounts (§10).
 *
 * Virtualized grid against the Phase 8 target of 5,000 accounts. The coverage column is
 * a badge, and filtering by coverage state is the primary way a user finds unmapped
 * accounts — so the filter is a row of buttons showing counts, not a dropdown you have
 * to go looking in.
 */
export function ChartOfAccounts() {
  const [search, setSearch] = useState('')
  const [coverage, setCoverage] = useState<CoverageState | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [selected, setSelected] = useState<Account | null>(null)
  const [creating, setCreating] = useState(false)

  const { data: accounts = [], isLoading } = useAccounts(search, coverage, includeInactive)
  const { data: allAccounts = [] } = useAccounts('', null, includeInactive)

  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: accounts.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
  })

  const counts = COVERAGE_FILTERS.reduce<Record<string, number>>((acc, f) => {
    acc[f.label] = f.value === null
      ? allAccounts.length
      : allAccounts.filter((a) => a.coverageState === f.value).length
    return acc
  }, {})

  return (
    <div className="flex h-full">
      <section className="flex min-w-0 flex-1 flex-col">
        <div
          className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
          style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
        >
          <h1 className="text-[14px] font-semibold">Chart of Accounts</h1>

          <input
            className="w-56 rounded border px-2 py-1 text-[12px]"
            style={{ borderColor: 'var(--color-rule)' }}
            placeholder="Search number or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="flex gap-0.5">
            {COVERAGE_FILTERS.map((f) => {
              const active = coverage === f.value
              return (
                <button
                  key={f.label}
                  onClick={() => setCoverage(f.value)}
                  title={f.hint}
                  className="rounded px-2 py-1 text-[12px]"
                  style={{
                    background: active ? 'var(--color-accent)' : 'transparent',
                    color: active ? '#fff' : 'var(--color-muted)',
                    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-rule)'}`,
                  }}
                >
                  {f.label}
                  <span className="num ml-1 opacity-70">{counts[f.label] ?? 0}</span>
                </button>
              )
            })}
          </div>

          <label className="flex items-center gap-1.5 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>

          <button
            className="ml-auto rounded border px-2 py-1 text-[12px]"
            style={{ borderColor: 'var(--color-rule)' }}
            onClick={() => {
              setCreating(true)
              setSelected(null)
            }}
          >
            New account
          </button>
        </div>

        <div
          className="grid shrink-0 border-b px-3 py-1.5 text-[11px] uppercase tracking-wide"
          style={{
            gridTemplateColumns: '110px 1fr 110px 150px 90px 90px',
            background: 'var(--color-panel-alt)',
            borderColor: 'var(--color-rule)',
            color: 'var(--color-muted)',
          }}
        >
          <span>Number</span>
          <span>Name</span>
          <span>Type</span>
          <span>Coverage</span>
          <span className="text-right">Mapped %</span>
          <span className="text-right">Drivers</span>
        </div>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
          {isLoading ? (
            <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
              Loading…
            </p>
          ) : accounts.length === 0 ? (
            <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
              No accounts match this filter.
            </p>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualizer.getVirtualItems().map((item) => {
                const account = accounts[item.index]
                if (!account) return null
                const isSelected = selected?.accountId === account.accountId

                return (
                  <div
                    key={account.accountId}
                    className="grid-row grid items-center border-b px-3"
                    data-selected={isSelected}
                    tabIndex={0}
                    role="row"
                    onClick={() => {
                      setSelected(account)
                      setCreating(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelected(account)
                        setCreating(false)
                      }
                    }}
                    style={{
                      gridTemplateColumns: '110px 1fr 110px 150px 90px 90px',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: ROW_HEIGHT,
                      transform: `translateY(${item.start}px)`,
                      borderColor: 'var(--color-rule-soft)',
                      opacity: account.isActive ? 1 : 0.55,
                    }}
                  >
                    <span className="font-mono text-[12px]">{account.accountNumber}</span>
                    <span className="truncate">{account.name}</span>
                    <span style={{ color: 'var(--color-muted)' }}>{account.accountType}</span>
                    <span>
                      <CoverageBadge state={account.coverageState} percent={account.totalMappedPercent} />
                    </span>
                    <span className="num" style={{ color: 'var(--color-muted)' }}>
                      {account.isAllocable ? formatPercent(account.totalMappedPercent) : '—'}
                    </span>
                    <span className="num" style={{ color: 'var(--color-muted)' }}>
                      {account.mappedDriverCount}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div
          className="shrink-0 border-t px-4 py-1.5 text-[11px]"
          style={{ background: 'var(--color-panel-alt)', borderColor: 'var(--color-rule-soft)', color: 'var(--color-muted)' }}
        >
          {accounts.length} account{accounts.length === 1 ? '' : 's'} shown
        </div>
      </section>

      <aside
        className="w-80 shrink-0 overflow-auto border-l"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        {creating ? (
          <AccountForm onDone={() => setCreating(false)} />
        ) : selected ? (
          <>
            <AccountForm
              key={selected.accountId}
              account={selected}
              onDone={() => setSelected(null)}
            />

            {/*
                Below the form and outside it, because it saves on its own. Classifying a
                chart is a pass down a list -- select, set two values, move on -- and
                making it share the form's Save button would put an extra click between
                every account and the next.
            */}
            <div
              className="border-t p-4"
              style={{ borderColor: 'var(--color-rule-soft)' }}
            >
              <ClassificationEditor
                key={`c-${selected.accountId}`}
                account={
                  accounts.find((a) => a.accountId === selected.accountId) ?? selected
                }
              />
            </div>
          </>
        ) : (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            Select an account to edit it.
          </p>
        )}
      </aside>
    </div>
  )
}

function AccountForm({ account, onDone }: { account?: Account; onDone: () => void }) {
  const isEdit = account !== undefined
  const [accountNumber, setAccountNumber] = useState(account?.accountNumber ?? '')
  const [name, setName] = useState(account?.name ?? '')
  const [accountType, setAccountType] = useState<AccountType>(account?.accountType ?? 'Expense')
  const [isAllocable, setIsAllocable] = useState(account?.isAllocable ?? true)
  const [isActive, setIsActive] = useState(account?.isActive ?? true)
  const [violations, setViolations] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  const create = useCreateAccount()
  const update = useUpdateAccount()
  const pending = create.isPending || update.isPending

  function onError(err: unknown) {
    setViolations(
      err instanceof ApiError
        ? Object.keys(err.violations).length > 0
          ? err.violations
          : { _: err.message }
        : { _: String(err) },
    )
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setViolations({})
    setSaved(false)

    const body = {
      accountNumber: accountNumber.trim(),
      name: name.trim(),
      accountType,
      isAllocable,
    }

    if (isEdit) {
      update.mutate(
        { id: account.accountId, ...body, isActive },
        { onSuccess: () => setSaved(true), onError },
      )
    } else {
      create.mutate(body, { onSuccess: onDone, onError })
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 p-4">
      <h2 className="text-[13px] font-semibold">{isEdit ? account.name : 'New account'}</h2>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>
          Account number
        </span>
        <input
          className="w-full rounded border px-2 py-1 font-mono text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
        />
        {violations.accountNumber && (
          <span className="text-[11px]" style={{ color: 'var(--color-error)' }}>
            {violations.accountNumber}
          </span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>
          Name
        </span>
        <input
          className="w-full rounded border px-2 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {violations.name && (
          <span className="text-[11px]" style={{ color: 'var(--color-error)' }}>
            {violations.name}
          </span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>
          Type
        </span>
        <select
          className="w-full rounded border px-2 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
          value={accountType}
          onChange={(e) => setAccountType(e.target.value as AccountType)}
        >
          {ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5 text-[12px]">
        <input
          type="checkbox"
          checked={isAllocable}
          onChange={(e) => setIsAllocable(e.target.checked)}
        />
        Allocable
      </label>

      {isEdit && (
        <label className="flex items-center gap-1.5 text-[12px]">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>
      )}

      {isEdit && account.isAllocable && (
        <dl
          className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]"
          style={{ color: 'var(--color-muted)' }}
        >
          <dt>Mapped</dt>
          <dd className="num">{account.totalMappedPercent.toFixed(6)}%</dd>
          <dt>Gap to 100</dt>
          <dd className="num" style={{ color: account.gapPercent === 0 ? undefined : 'var(--color-warn)' }}>
            {account.gapPercent.toFixed(6)}%
          </dd>
          <dt>Drivers</dt>
          <dd className="num">{account.mappedDriverCount}</dd>
        </dl>
      )}

      {violations._ && (
        <p className="text-[12px]" style={{ color: 'var(--color-error)' }}>
          {violations._}
        </p>
      )}
      {saved && (
        <p className="text-[12px]" style={{ color: 'var(--color-ok)' }}>
          Saved.
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={pending}
          className="rounded px-3 py-1 text-[12px] font-medium text-white"
          style={{ background: 'var(--color-accent)' }}
        >
          {pending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded border px-3 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-rule)' }}
        >
          {isEdit ? 'Close' : 'Cancel'}
        </button>
      </div>
    </form>
  )
}
